import { DEFAULT_LANGUAGE } from "@/data/constants";
import { CSSProperties, ReactNode } from "react";

interface TypographyProps {
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body1"
    | "body2"
    | "body3"
    | "subtitle1"
    | "subtitle2"
    | "label";
  color?: string;
  className?: string;
  children?: ReactNode;
  language?: AppLocale;
  style?: CSSProperties;
  id?: string;
}

const elementMapping: {
  [key in NonNullable<TypographyProps["variant"]>]: keyof JSX.IntrinsicElements;
} = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  body3: "p",
  label: "p",
};

export const Typography = ({
  variant = "body1", // don't change default
  color,
  className,
  language = DEFAULT_LANGUAGE,
  children,
  style,
  ...props
}: TypographyProps) => {
  const Component = elementMapping[variant] as keyof JSX.IntrinsicElements;

  const headingVariants: { [key in NonNullable<TypographyProps["variant"]>]: string } = {
    h1: "text-[25px] md:text-[27px] leading-[1.3] md:leading-[1.3] font-[600]",
    h2: "text-[22px] md:text-[24px] leading-[1.3] md:leading-[1.3] font-[600]",
    h3: "text-[20px] md:text-[22px] leading-[1.3] md:leading-[1.3] font-[600]",
    h4: "text-[18px] md:text-[20px] font-[600] leading-[1.3] md:leading-[1.3]",
    h5: "text-[16px] md:text-[18px] font-[600] leading-[1.3] md:leading-[1.3]",
    h6: "text-[14px] md:text-[16px] font-[600] leading-[1.3] md:leading-[1.3]",
    subtitle1: "text-[17px] md:text-lg font-medium leading-[1.3] md:leading-[1.3]",
    subtitle2: "text-[15px] md:text-base font-medium leading-[1.3] md:leading-[1.3]",
    body1: "text-base leading-[26px] md:leading-[26px]",
    body2: "text-[14px] md:text-sm leading-[1.5]",
    body3: "text-xs leading-[1.5]",
    label: "text-[15px] md:text-base lg:text-[13px] leading-[24px]",
  };

  /**
   * Larger sizes for Telugu script readability.
   * Ramabhadra only ships as Regular (400) and is already a heavy display face —
   * do not apply 600/bold (browser synthetic bold makes glyphs look too thick).
   * `!` needed because Tailwind is configured with `important: true`.
   */
  const teluguVariants: { [key in NonNullable<TypographyProps["variant"]>]: string } = {
    h1: "text-[28px] md:text-[32px] leading-[1.35] !font-normal",
    h2: "text-[24px] md:text-[28px] leading-[1.35] !font-normal",
    h3: "text-[22px] md:text-[24px] leading-[1.35] !font-normal",
    h4: "text-[20px] md:text-[22px] !font-normal leading-[1.35]",
    h5: "text-[18px] md:text-[20px] !font-normal leading-[1.35]",
    h6: "text-[16px] md:text-[18px] !font-normal leading-[1.35]",
    subtitle1: "text-[19px] md:text-xl !font-normal leading-[1.4]",
    subtitle2: "text-[17px] md:text-lg !font-normal leading-[1.4]",
    body1: "text-lg md:text-[19px] leading-[32px] md:leading-[34px]",
    body2: "text-base md:text-[17px] leading-[1.65]",
    body3: "text-sm leading-[1.6]",
    label: "text-base md:text-[17px] leading-[28px]",
  };

  const variants = language === "te" ? teluguVariants : headingVariants;

  const isHeadingVariant =
    variant === "h1" ||
    variant === "h2" ||
    variant === "h3" ||
    variant === "h4" ||
    variant === "h5" ||
    variant === "h6" ||
    variant === "subtitle1" ||
    variant === "subtitle2";

  const fontClass =
    language === "te" ? (isHeadingVariant ? "font-title" : "font-content") : "font-sans";

  const typographyClasses = ` ${!color ? " text-[#36454F] dark:text-white " : " "}  ${
    variants[variant]
  } ${fontClass} ${className}`;

  return (
    <Component
      style={{ color: color ? color : "", ...(style || {}) }}
      className={typographyClasses}
      {...props}
    >
      {children}
    </Component>
  );
};
